/**
 * Phase 25 — Progressive-lock gate predicates.
 *
 * Covers: GATE25-01 (canUnlockPricing), GATE25-02 (canUnlockAmenities + auto-unlock for Credits/Access).
 *
 * Implemented in Plan 25-04. Wave 0 stub bodies replaced with real logic.
 */

import type { BasicsState, PricingState, PlanEditorState } from './types';

/**
 * GATE25-01 — Pricing unlocks when Basics is committed with a valid required field set.
 *
 * Required: non-empty planName, non-empty cadence, monthlyPrice parses to a
 * finite number > 0, AND basicsSaved=true.
 *
 * Resource Type is intentionally NOT required: "no space hold" (null/empty) is a
 * valid plan configuration (Event/Shooting plans, virtual offices) and the DB
 * column is nullable. Gating on it made such plans impossible to create and
 * permanently un-editable below Basics once saved.
 */
export function canUnlockPricing(basics: BasicsState): boolean {
  if (!basics.planName.trim()) return false;
  if (!basics.cadence) return false;
  const monthly = parseFloat(basics.monthlyPrice);
  if (!Number.isFinite(monthly) || monthly <= 0) return false;
  if (!basics.basicsSaved) return false;
  return true;
}

/**
 * GATE25-02 — Amenities unlocks once Pricing has been committed.
 * Tax + period overrides DO NOT block (overrides optional per CONTEXT).
 */
export function canUnlockAmenities(pricing: PricingState, basics: BasicsState): boolean {
  return canUnlockPricing(basics) && pricing.pricingSaved;
}

/**
 * GATE25-02 — Credits auto-unlocks once Pricing is saved (Amenities optional).
 */
export function canUnlockCredits(state: PlanEditorState): boolean {
  return canUnlockAmenities(state.pricing, state.basics);
}

/**
 * GATE25-02 — Access auto-unlocks once Pricing is saved (Amenities optional).
 */
export function canUnlockAccess(state: PlanEditorState): boolean {
  return canUnlockAmenities(state.pricing, state.basics);
}
